"use client";

import { useMemo } from "react";

import { getSellableMembershipPlan } from "@/consts/membership";
import { hasProductFeatureBypassClient } from "@/lib/product-feature-bypass-client";
import { useAuth } from "@/providers/auth";
import type { PlanFeatureId } from "@/types/membership";

/**
 * Hook to check if the current user has access to a specific feature
 * Returns true if:
 * - User has product.features.bypass (admin or granted support), OR
 * - User has an active membership with a plan that includes the feature
 */
export function useHasFeatureAccess(featureId: PlanFeatureId): boolean {
	const { user } = useAuth();

	return useMemo(() => {
		if (!user) return false;

		if (hasProductFeatureBypassClient(user.role, user.permissions)) return true;

		// Check if membership is active
		if (!user.membership.isActive) return false;

		// Check if membership has expired
		if (user.membership.endDate && user.membership.endDate < new Date()) {
			return false;
		}

		if (user.membership.featureIds?.length) {
			return user.membership.featureIds.includes(featureId);
		}

		const plan = getSellableMembershipPlan(user.membership.planId);
		if (!plan) return false;

		return plan.features.includes(featureId);
	}, [user, featureId]);
}
